import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  ButtonExportCSVAsync,
  Card,
  CardContent,
  CardIconStatus,
  CardLabel,
  CardValue,
  Cell,
  DateRangePicker,
  Empty,
  FieldWrapper,
  Filter,
  FilterButton,
  HeaderCell,
  HeaderRow,
  Input,
  Label,
  PaginationFooter,
  Row,
  Section,
  SortLabel,
  Table,
  TableBody,
  TableHead,
} from 'components';
import { Appearance } from 'design/types';
import ListContext, { ListContextProvider } from 'providers/ListContextProvider';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { selectSiteAcceptance } from 'store/Site';
import { getAcceptancePaymentStatsRequest, selectTipAcceptanceStats, Stats } from 'store/Stats';
import { getPaymentsRequest, selectTipAcceptancePayments, TipAcceptance } from 'store/TipAcceptance';
import { IStore } from 'store/types';
import { ApiQueryOperator, IAcceptancePayment, IApiQueryParam } from 'types';
import { CSVFormatter } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi, formatDateForUI, getTimeWithZone } from 'utils/date';
import { calculateAcceptancePayoutNet } from 'utils/payout';
import * as API from '../../api';
import { AcceptancePaymentRow, CardRowWrapper, ReportTableHeader } from './components';

interface IDependenciesType {
  currentSiteIdDep: string;
  acceptanceEnabled?: boolean;
}

const ReportsAcceptancePayments = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const DEFAULT_END_DATE = dayjs().format('MM-DD-YYYY');
  const DEFAULT_START_DATE = dayjs().subtract(6, 'day').format('MM-DD-YYYY');
  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId;
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const paymentStats = useSelector(selectTipAcceptanceStats());
  const isStatsLoading = useSelector((state: IStore) => !paymentStats || selectIsLoadingAPIRequest(state, Stats.GET_TIP_ACCEPTANCE_PAYMENT_STATS_REQUEST));
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, TipAcceptance.GET_PAYMENTS_REQUEST));
  const payments = useSelector(selectTipAcceptancePayments());
  const total = payments?.total;
  const [filterActive, setFilterActive] = useState<boolean>(false);

  const defaultFilters = [
    {
      key: 'payment_date',
      operator: ApiQueryOperator.gte,
      value: formatDateForApi(DEFAULT_START_DATE),
    },
    {
      key: 'payment_date',
      operator: ApiQueryOperator.lte,
      value: formatDateForApi(DEFAULT_END_DATE),
    },
  ];

  useEffect(() => {
    if (!currentSiteId || (currentSiteAcceptance && !currentSiteAcceptance.enabled)) {
      history.push(routeConstants.private.reportsPaidPayments);
    }
  }, [history, currentSiteId, currentSiteAcceptance]);

  const fetchStats = useCallback(
    (query: IApiQueryParam[]) => {
      const startDate = (query || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
      const endDate = (query || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

      const paramsWithoutPaymentDate = {
        q: query?.filter((q) => q.key !== 'payment_date'),
      };

      dispatch(getAcceptancePaymentStatsRequest(currentSiteId, startDate.toString(), endDate.toString(), paramsWithoutPaymentDate));
    },
    [currentSiteId]
  );

  const fetchData = (queryParams: IApiQueryOptions) => {
    const meta = queryParams.meta as IDependenciesType;
    const { currentSiteIdDep, acceptanceEnabled } = meta;

    if (currentSiteIdDep && acceptanceEnabled) {
      const queryParamsWithoutMeta = omit(queryParams, 'meta');
      dispatch(getPaymentsRequest(currentSiteIdDep, queryParamsWithoutMeta));
      fetchStats(queryParamsWithoutMeta.q);
    }
  };

  const exportCSV = async (siteId: string, options: IApiQueryOptions) => {
    const response = await API.Sites.getTipAcceptancePayments(siteId, { ...options, limit: 0, offset: 0 });
    return CSVFormatter.acceptancePayments(response.body);
  };

  return (
    <>
      <CardRowWrapper>
        <Card data-cy="tip-amount-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="DollarFill" isLoading={isStatsLoading} />
          <CardContent>
            <CardLabel isLoading={isStatsLoading}>Tip Amount</CardLabel>
            <CardValue isLoading={isStatsLoading}>{centsToDollarsDisplay(paymentStats?.gross || 0)}</CardValue>
          </CardContent>
        </Card>
        <Card data-cy="fees-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="DollarFill" isLoading={isStatsLoading} />
          <CardContent>
            <CardLabel isLoading={isStatsLoading}>Fees</CardLabel>
            <CardValue isLoading={isStatsLoading}>{centsToDollarsDisplay(paymentStats?.fees.total || 0)}</CardValue>
          </CardContent>
        </Card>
        <Card data-cy="net-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="DollarFill" isLoading={isStatsLoading} />
          <CardContent>
            <CardLabel isLoading={isStatsLoading}>Net Payments</CardLabel>
            <CardValue isLoading={isStatsLoading}>{centsToDollarsDisplay(paymentStats?.net || 0)} </CardValue>
          </CardContent>
        </Card>
      </CardRowWrapper>

      {currentSiteId && (
        <ListContextProvider
          fetchData={fetchData}
          totalSelector={() => total}
          defaultOffset={0}
          defaultLimit={10}
          defaultSort="created_at"
          dependencies={{
            currentSiteIdDep: currentSiteId,
            acceptanceEnabled: currentSiteAcceptance?.enabled,
          }}
          defaultFilters={defaultFilters}
          dataKey="reportsAcceptancePayments"
        >
          <ListContext.Consumer>
            {({ methods, query, components }) => {
              const { onFiltersChange, onSortChange, isSortDesc } = methods;
              const { resultsPerPage: ResultsPerPage, pagination: Pager } = components;
              const debounceMs = 500;

              const { q } = query;
              const queryParamsWithoutMeta = omit(query, 'meta');
              const isDescendingSort = isSortDesc();

              const setDates = ([start, end]: string[]) => {
                const otherFilters = (q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');
                const isValid = dayjs(start, 'YYYY-MM-DD', true).isValid() && dayjs(end, 'YYYY-MM-DD', true).isValid();
                if (isValid) {
                  (() => {
                    const filters = [
                      ...otherFilters,
                      {
                        key: 'payment_date',
                        operator: ApiQueryOperator.gte,
                        value: start,
                      },
                      {
                        key: 'payment_date',
                        operator: ApiQueryOperator.lte,
                        value: end,
                      },
                    ];
                    onFiltersChange(filters, true);
                    fetchStats(filters);
                  })();
                }
              };

              const onFilterChange = (value: string, key: string) => {
                const otherFilters = (q || []).filter((query: any) => query.key !== key) as IApiQueryParam[];
                const newValue = value.trim();
                const filters = newValue
                  ? [
                      ...otherFilters,
                      {
                        key,
                        operator: ApiQueryOperator.like,
                        value: newValue,
                      },
                    ]
                  : otherFilters;
                onFiltersChange(filters);
                fetchStats(filters);
              };

              const debouncedOnFilterChange = debounce(onFilterChange, debounceMs);

              const hasResults = total > 0;
              const qWithoutDates = (q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');

              return (
                <Section data-cy="table">
                  <ReportTableHeader
                    title="Results"
                    count={total || 0}
                    actions={
                      <>
                        <Label>Business Day:</Label>
                        <DateRangePicker fluidWidth={false} inputHeight="small" onSubmit={setDates} pickerPosition="right" startDate={DEFAULT_START_DATE} endDate={DEFAULT_END_DATE} submitLabel="apply" />
                        <FilterButton filterActive={filterActive} appliedFilters={qWithoutDates.length} onClick={() => setFilterActive(!filterActive)} />
                        <ButtonExportCSVAsync disabled={!hasResults} exportFunc={() => exportCSV(currentSiteId, queryParamsWithoutMeta)} fileName={`${currentSiteId}_acceptance_payments`} />
                      </>
                    }
                    isLoading={isLoading}
                  />
                  <Filter isVisible={filterActive}>
                    <FieldWrapper label="Cardholder" data-cy="filter-cardholder">
                      <Input placeholder="Search" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnFilterChange(e.currentTarget.value, 'payor.name')} />
                    </FieldWrapper>
                    <FieldWrapper label="Transaction ID" data-cy="filter-transaction-id">
                      <Input placeholder="Search" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnFilterChange(e.currentTarget.value, 'id')} />
                    </FieldWrapper>
                    <FieldWrapper label="Employee" data-cy="filter-employee">
                      <Input placeholder="Search" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnFilterChange(e.currentTarget.value, 'employee_name')} />
                    </FieldWrapper>
                  </Filter>
                  <Table sticky scrollable>
                    <TableHead>
                      <HeaderRow sticky>
                        <HeaderCell>
                          <SortLabel data-cy="date-header" label="Date" isSorted isSortedDesc={isDescendingSort} onClick={() => onSortChange('created_at')} isLoading={isLoading} />
                        </HeaderCell>
                        <HeaderCell data-cy="cardholder-header">{!isLoading && 'Cardholder'}</HeaderCell>
                        <HeaderCell data-cy="transaction-id-header">{!isLoading && 'Transaction ID'}</HeaderCell>
                        <HeaderCell data-cy="employee-header">{!isLoading && 'Employee'}</HeaderCell>
                        <HeaderCell data-cy="tip-amount-header">{!isLoading && 'Tip Amount'}</HeaderCell>
                        <HeaderCell data-cy="fees-header">{!isLoading && 'Fees'} </HeaderCell>
                        <HeaderCell data-cy="net-header">{!isLoading && 'Net'}</HeaderCell>
                      </HeaderRow>
                    </TableHead>

                    <TableBody>
                      {payments?.data.length === 0 ? (
                        <Row>
                          <Cell colSpan={8}>
                            <Empty container containerHeight={300} icon="Payment" message="No results. Try a different date range." />
                          </Cell>
                        </Row>
                      ) : (
                        payments?.data.map((datum: IAcceptancePayment) => (
                          <AcceptancePaymentRow
                            key={`acceptance-payment-${datum.id}`}
                            data-cy="acceptance-payment-row"
                            data-value={datum.id}
                            date={formatDateForUI(datum.createdAt)}
                            time={getTimeWithZone(datum.createdAt)}
                            cardholder={datum.payor?.name || ''}
                            trxId={datum.id}
                            employeeName={datum.employee?.name || ''}
                            tipAmount={datum.amount}
                            fees={datum.fees.total}
                            net={calculateAcceptancePayoutNet(datum)}
                            isLoading={isLoading}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <PaginationFooter>
                    {!isLoading && !!total && total > query.limit && ResultsPerPage}
                    {!isLoading && !!total && total > query.limit && Pager}
                  </PaginationFooter>
                </Section>
              );
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}
    </>
  );
};

export default ReportsAcceptancePayments;
